import type { OrganizationDetail } from '../../../api';
import type { OrganizationRuntimeState } from '../../scenarioTask/components/runningProcess/types';
import type { OrgNodeData, OrganizationGraphCompileResult } from '../../scenarioTask/components/runningProcess/utils/organizationGraph';
import type { OrganizationStreamItem } from './stream';

/** 组织图完全点亮后的人员状态。 */
type PersonLitStatus = Extract<OrganizationRuntimeState['status'], 'applied' | 'approved'>;

/** 根据已编译图产物，生成“完整点亮”的组织图状态。 */
export function buildFullyLitOrgStates(
  graph: OrganizationGraphCompileResult,
  personLitStatus: PersonLitStatus = 'approved'
): Map<string, OrganizationRuntimeState> {
  const next = new Map<string, OrganizationRuntimeState>();

  graph.nodeMap.forEach((node, id) => {
    next.set(id, {
      status: node.kind === 'person' ? personLitStatus : 'applied',
    });
  });

  return next;
}

/** 判定输入 JSON 是否至少具备组织图构建所需的基础结构。 */
export function isOrganizationDetailPayload(
  value: unknown
): value is OrganizationDetail {
  if (!value || typeof value !== 'object') return false;
  const detail = value as Partial<OrganizationDetail>;
  if (!detail.organization || typeof detail.organization !== 'object') return false;
  const organization = detail.organization as Partial<OrganizationDetail['organization']>;
  return typeof organization.name === 'string';
}

/** 将创建组织流事件映射为组织图状态点亮。 */
export function applyCreateStreamItemToStates(
  prev: Map<string, OrganizationRuntimeState>,
  graph: OrganizationGraphCompileResult,
  item: OrganizationStreamItem
): Map<string, OrganizationRuntimeState> {
  if (item.type !== 'log' || item.category !== 'organization') return prev;

  const next = new Map(prev);

  if (item.content === '完成初始化') {
    setNodeStatusByName(next, graph, item.name, 'approved', 'person');
    return next;
  }

  if (item.content === '完成结构') {
    setNodeStatusByName(next, graph, item.name, 'applied');
    return next;
  }

  if (item.content === '完成构造结构') {
    setNodeStatusByName(next, graph, item.name, 'applied', 'company');
    return next;
  }

  return prev;
}

/** 将解散组织流事件映射为组织图状态点暗。 */
export function applyDissolveStreamItemToStates(
  prev: Map<string, OrganizationRuntimeState>,
  graph: OrganizationGraphCompileResult,
  item: OrganizationStreamItem
): Map<string, OrganizationRuntimeState> {
  if (item.type !== 'log' || item.category !== 'organization') return prev;

  const next = new Map(prev);

  if (item.content === '完成销毁') {
    removeNodeStateByName(next, graph, item.name);
    return next;
  }

  if (item.content === '完成解构结构') {
    const fallbackKind =
      item.name === getCompanyName(graph) || prev.size <= 1 ? 'company' : undefined;
    removeNodeStateByName(next, graph, item.name, fallbackKind);
    return next;
  }

  return prev;
}

function setNodeStatusByName(
  target: Map<string, OrganizationRuntimeState>,
  graph: OrganizationGraphCompileResult,
  name: string,
  status: OrganizationRuntimeState['status'],
  fallbackKind?: OrgNodeData['kind']
) {
  const ids = findNodeIds(graph, name, fallbackKind);
  ids.forEach(id => {
    target.set(id, { status });
  });
}

function removeNodeStateByName(
  target: Map<string, OrganizationRuntimeState>,
  graph: OrganizationGraphCompileResult,
  name: string,
  fallbackKind?: OrgNodeData['kind']
) {
  const ids = findNodeIds(graph, name, fallbackKind);
  ids.forEach(id => {
    target.delete(id);
  });
}

function findNodeIds(
  graph: OrganizationGraphCompileResult,
  name: string,
  fallbackKind?: OrgNodeData['kind']
): string[] {
  const ids = graph.nameToIds.get(name);
  if (ids && ids.length > 0) return ids;

  if (!fallbackKind) return [];

  const matched: string[] = [];
  graph.nodeMap.forEach((node, id) => {
    if (node.kind === fallbackKind) {
      matched.push(id);
    }
  });
  return matched;
}

function getCompanyName(graph: OrganizationGraphCompileResult): string {
  for (const node of graph.nodeMap.values()) {
    if (node.kind === 'company') return node.originalName;
  }
  return '';
}
